// What a page reviews: a pull request, a compare of two branches, or one
// branch's history (docs/design.md "Subjects"). A subject does two things
// only: it produces a Track (logic/track.ts), and it names its facets, the
// features that exist for it. Facets are decided here, by kind, and nowhere
// else: views ask for a facet, never test the kind.

export interface PullSubject {
  kind: 'pull';
  owner: string;
  repo: string;
  number: number;
}

export interface CompareSubject {
  kind: 'compare';
  owner: string;
  repo: string;
  /** The base branch, or null for the repository's default branch (a compare URL without one). */
  base: string | null;
  /** The head branch, in `headRepo`. */
  head: string;
  /**
   * Where the head branch lives when it is not this repository (GitHub's
   * cross-fork compare, `main...ana:feat` or `main...ana:fork:feat`): its
   * owner, and its repository's name, or null for the owner's fork of this
   * repository (GitHub finds it). Null for a head in this repository.
   */
  headRepo: { owner: string; repo: string | null } | null;
}

/**
 * A branch's (or tag's, or commit's) history, resolved: the ref, and the
 * path its history is filtered to (GitHub's file history), or null.
 */
export interface RefSubject {
  kind: 'ref';
  owner: string;
  repo: string;
  /** A branch or tag name, or a commit SHA, as the repository has it. */
  ref: string;
  /** A path in the ref whose history alone is shown, or null for all of it. */
  path: string | null;
}

/**
 * What a GitHub `commits/<spec>` URL names before it is resolved: whatever
 * followed `/commits/`, a ref (a branch or tag name, a commit SHA, or
 * `HEAD` for the default branch) optionally followed by a path in it.
 * Ref names hold `/` too, so only the repository's real refs tell where
 * the ref ends: the ref subject's loader resolves a locator against
 * GitHub, the longest existing ref winning and the rest becoming the
 * path, into a `RefSubject`. A locator is no subject: it has no key, no
 * facets, no host and no grant, so nothing can be kept or granted under a
 * name whose meaning changes when the repository gains a branch.
 */
export interface RefLocator {
  owner: string;
  repo: string;
  spec: string;
  /**
   * The path, when the URL said where the ref ends (router.ts
   * `subjectPath`, `?under=`): the spec is then the ref whole, and nothing is resolved
   * longest first.
   */
  path?: string;
}

export type Subject = PullSubject | CompareSubject | RefSubject;
export type SubjectKind = Subject['kind'];

/**
 * Whether `ref` can name a branch, a tag or a commit SHA in a subject: a
 * name `git check-ref-format --branch` takes (a SHA, 7 to 40 hex digits,
 * is one): not empty, `@` or `HEAD`; no leading `-`; no component
 * starting with `.` or ending in `.lock`; no `..`, `//`, leading or
 * trailing `/`, or trailing `.`; no control character, space, `:`, `?`,
 * `*`, `[` or `\\`. Relative revisions (`@{…}`, `^`, `~`) are refused
 * deliberately, though GitHub's compare takes them: they name different
 * commits over time, so they can be no subject's key or grant. (`HEAD`
 * as a ref spec's whole first component, the default branch, is the
 * router's one exception.)
 */
export function isRefName(ref: string): boolean {
  if (!ref || ref === '@' || ref === 'HEAD' || ref.startsWith('-') || ref.endsWith('.') || ref.includes('..') || ref.includes('@{')) return false;
  if (/[\x00-\x20\x7f~^:?*[\\]/.test(ref)) return false;
  return ref.split('/').every((c) => c !== '' && !c.startsWith('.') && !c.endsWith('.lock'));
}

/**
 * Whether `name` can be a GitHub owner or repository in a subject: at most
 * 100 of letters, digits, `.`, `_` and `-`, and not dots alone (`..` would
 * climb a REST path).
 */
export function isGitHubName(name: string): boolean {
  return /^[A-Za-z0-9_.-]{1,100}$/.test(name) && !/^\.+$/.test(name);
}

/** Whether `path` can be a ref subject's path: components not empty, `.` or `..`, no control characters. */
export function isRefPath(path: string): boolean {
  return path.length > 0 && !/[\x00-\x1f\x7f]/.test(path) && path.split('/').every((c) => c !== '' && c !== '.' && c !== '..');
}

/**
 * The path a subject's content is confined to: a ref's history filtered to
 * a path (GitHub's file history) lists only the commits that touch it, and
 * its Files view only the files in it; null for every other subject, and
 * a ref's history of the whole tree.
 */
export function pathOf(subject: Subject): string | null {
  return subject.kind === 'ref' ? subject.path : null;
}

/** Whether `file` lies in `path` (the path itself, or below it); every file does in no path (null). */
export function inPath(path: string | null, file: string): boolean {
  return path === null || file === path || file.startsWith(path + '/');
}

/** A compare's head as GitHub writes it: `feat`, `ana:feat`, or `ana:fork:feat`. */
export function compareHead(s: CompareSubject): string {
  if (!s.headRepo) return s.head;
  return `${s.headRepo.owner}:${s.headRepo.repo === null ? '' : s.headRepo.repo + ':'}${s.head}`;
}

/** The features that exist for a subject, and how its pages speak of it. */
export interface Facets {
  /** The Files and Commits views, over a range of the subject's track. */
  readonly range: boolean;
  /** Review marks and private notes on the content shown. */
  readonly marks: boolean;
  readonly notes: boolean;
  /** Checks on a commit. */
  readonly checks: boolean;
  /** GitHub's conversation: threads, reviewers, approval, merge, the attention set and the Viewed sync. */
  readonly conversation: boolean;
  /** Drafts, and publishing them as a review. */
  readonly drafts: boolean;
  /** The viewer's submitted reviews, which "Since my last review" starts from. */
  readonly reviews: boolean;
  /** The pull requests that brought each commit in. */
  readonly linkedPulls: boolean;
  /**
   * The range a view opens on when its URL names no `from`: `all`, base →
   * head (the whole change), or `latest`, the head's parent → head (a
   * branch's latest commit; its base is only where the window starts).
   */
  readonly opens: 'all' | 'latest';
  /** What a sentence calls the subject: a pull request, or the branch a compare or ref is about. */
  readonly noun: 'pull request' | 'branch';
  /**
   * What its track's points are called: a pull request's `snapshots` (its
   * heads, a force-pushed one included, which its comments name), a branch's
   * `commits`.
   */
  readonly points: 'snapshots' | 'commits';
}

const FACETS: Record<SubjectKind, Facets> = {
  pull: Object.freeze({ range: true, marks: true, notes: true, checks: true, conversation: true, drafts: true, reviews: true, linkedPulls: false, opens: 'all', noun: 'pull request', points: 'snapshots' }),
  compare: Object.freeze({ range: true, marks: true, notes: true, checks: true, conversation: false, drafts: false, reviews: false, linkedPulls: true, opens: 'all', noun: 'branch', points: 'commits' }),
  ref: Object.freeze({ range: true, marks: true, notes: true, checks: true, conversation: false, drafts: false, reviews: false, linkedPulls: true, opens: 'latest', noun: 'branch', points: 'commits' }),
};

/** The features `subject` has. */
export function facets(subject: Subject): Facets {
  return FACETS[subject.kind];
}

/** The features every subject of `kind` has (what a grant on one may cover, api/relaySubject.ts). */
export function facetsOf(kind: SubjectKind): Facets {
  return FACETS[kind];
}

/**
 * The subject as GitHub names it in its canonical spelling: owner and
 * repository lowercased wherever they appear (GitHub's names are
 * case-insensitive), a fork's head's included, and a head in this very
 * repository written without its owner (`o:feat` on `o/r` is `feat`).
 * Branch names, tags and paths stay as written: git's are case-sensitive.
 */
export function canonicalSubject<S extends Subject>(subject: S): S;
export function canonicalSubject(subject: Subject): Subject {
  const owner = subject.owner.toLowerCase();
  const repo = subject.repo.toLowerCase();
  if (subject.kind !== 'compare') return { ...subject, owner, repo };
  const h = subject.headRepo && { owner: subject.headRepo.owner.toLowerCase(), repo: subject.headRepo.repo?.toLowerCase() ?? null };
  const here = !!h && h.owner === owner && (h.repo === null || h.repo === repo);
  return { ...subject, owner, repo, headRepo: here ? null : h };
}

/**
 * The subject's one key, `owner/repo#12`, `owner/repo@base...head`
 * (`owner/repo@head` when its URL names no base: the default branch is not
 * resolved into the key; `...` always, never `..`) or `owner/repo:ref`,
 * with `//path` after it for a ref's history filtered to a path (`//`
 * cannot occur in a ref name, so the two parts never blur), all in the
 * subject's canonical spelling (`canonicalSubject`): so one subject has
 * exactly one key. Remote Relay's wire form and match key, the key of what
 * the browser keeps per subject, and, without its `owner/repo` (and a
 * ref's `//path`), the subject a review flag is stored on (docs/superpowers/
 * specs/2026-11-01-subjects-design.md, "Subject keys"). Only a resolved
 * subject has a key: a `RefLocator` has none.
 */
export function subjectKey(subject: Subject): string {
  const s = canonicalSubject(subject);
  const repo = `${s.owner}/${s.repo}`;
  switch (s.kind) {
    case 'pull':
      return `${repo}#${s.number}`;
    case 'compare':
      return s.base === null ? `${repo}@${compareHead(s)}` : `${repo}@${s.base}...${compareHead(s)}`;
    case 'ref':
      return `${repo}:${s.ref}${s.path === null ? '' : '//' + s.path}`;
  }
}


/** How a page names its subject in passing: `o/r / PR 12`, `o/r / main...feat/x`, `o/r / feat/x`. */
export function subjectLabel(subject: Subject): string {
  const repo = `${subject.owner}/${subject.repo}`;
  switch (subject.kind) {
    case 'pull':
      return `${repo} / PR ${subject.number}`;
    case 'compare':
      return `${repo} / ${subject.base === null ? '' : subject.base + '...'}${compareHead(subject)}`;
    case 'ref':
      return `${repo} / ${subject.ref}${subject.path === null ? '' : ' · ' + subject.path}`;
  }
}

